import { StoryblokComponent } from '@storyblok/react';
import { FormEvent, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { SubmitHandler, useForm } from 'react-hook-form';

import { JsonObject } from '../../types/jsonObject';
import { getColorFromStory } from '../../utils/getColorFromStory';
import { getSpacingFromStory } from '../../utils/getSpacingFromStory';
import { patchedStoryblokEditable } from '../../utils/patchedStoryblokEditable';
import { FormBuilderContextProvider } from './FormBuilder.context';
import {
  FormBuilderFormErrorMessage,
  FormBuilderFormSuccessMessage,
  FormBuilderWrapper,
} from './FormBuilder.styles';
import { FormBuilderProps, FormBuilderRequestLifecycleContext } from './FormBuilder.types';
import { useFormBuilderState } from './hooks/useFormBuilderState';

export const FormBuilder = (props: FormBuilderProps) => {
  const {
    blok,
    defaultFieldValues = [],
    defaultRequestHeaders,
    onSubmit,
    onSubmitError,
    onSubmitSuccess,
  } = props;
  const {
    actionUrl,
    requestHeaders = [],
    actionHttpMethod = 'POST',
    submitDataAs,
    errorMessageField = 'message',
    body,
    backgroundColor,
    maxWidth,
    borderRadius,
    enableShadow,
    horizontalPadding,
    horizontalPaddingMobile,
    verticalPadding,
    verticalPaddingMobile,
  } = blok;

  const {
    keyValues,
    keysIncludedInSubmitRequest,
    setKeyValues,
    setKeyValue,
    includeKeyInSubmitRequest,
    getFinalRequestData,
  } = useFormBuilderState(defaultFieldValues);

  const defaultFieldValuesSyncKey = useMemo(
    () =>
      JSON.stringify(
        (defaultFieldValues ?? []).map(({ key: fieldKey, value }) => [
          fieldKey,
          value != null ? String(value) : '',
        ])
      ),
    [defaultFieldValues]
  );

  useEffect(() => {
    if (!defaultFieldValues?.length) {
      return;
    }
    setKeyValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const { key: fieldKey, value } of defaultFieldValues) {
        const str = value != null ? String(value) : '';
        if (!str) {
          continue;
        }
        const current = prev[fieldKey];
        if (current === undefined || current === null || current === '') {
          next[fieldKey] = str;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    for (const { key: fieldKey, includeInSubmitRequest: include } of defaultFieldValues) {
      if (include) {
        includeKeyInSubmitRequest(fieldKey);
      }
    }
    // defaultFieldValues is read from the latest render; defaultFieldValuesSyncKey avoids stale closures when the array reference changes without content changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- includeKeyInSubmitRequest/setKeyValues are stable setState updaters; omitting them prevents effect running every render.
  }, [defaultFieldValuesSyncKey]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hookForm = useForm({ mode: 'all' });

  const createRequestData = (): FormData | JsonObject | null => {
    const requestData = getFinalRequestData();

    if (submitDataAs === 'json') {
      // is already a plain JS object, i.e. - JSON
      return requestData;
    }

    if (submitDataAs === 'formData') {
      const formData = new FormData();

      for (const key in requestData) {
        formData.append(key, requestData[key].toString());
      }

      return formData;
    }

    return null;
  };

  const handleFormSubmit: SubmitHandler<unknown> = async (_, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setErrorMessage(null);
    setSuccessMessage(null);

    const data = createRequestData();

    if (!data) {
      return;
    }

    const headers = requestHeaders.reduce(
      (acc, header) => ({
        ...acc,
        [header.name]: header.value,
      }),
      {}
    );

    const requestLifecycleContext: FormBuilderRequestLifecycleContext = {
      keyValues,
      keysIncludedInSubmitRequest,
      setKeyValue,
      includeKeyInSubmitRequest,
      getFinalRequestData,
      hookForm,
      setErrorMessage,
      setSuccessMessage,
    };

    // if onSubmit is provided, we assume it contains all necessary requests and error handling;
    // so handle it separately, instead of doing HTTP calls internally.
    if (onSubmit) {
      try {
        await onSubmit(data, requestLifecycleContext);
        return;
      } catch (error) {
        console.log(error);
      }
    }

    // if onSubmit is not provided, we rely on the internal logic and error handling
    try {
      const response = await axios(actionUrl, {
        method: actionHttpMethod,
        data,
        headers: { ...defaultRequestHeaders, ...headers },
      });
      if (onSubmitSuccess) {
        await onSubmitSuccess(response, requestLifecycleContext);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.[errorMessageField]);
      if (onSubmitError) {
        await onSubmitError(error, requestLifecycleContext);
      }
    }
  };

  return (
    <FormBuilderContextProvider
      value={{
        keyValues,
        keysIncludedInSubmitRequest,
        setKeyValue,
        includeKeyInSubmitRequest,
        getFinalRequestData,
        hookForm,
      }}
    >
      <FormBuilderWrapper
        {...patchedStoryblokEditable(blok)}
        onSubmit={hookForm.handleSubmit(handleFormSubmit)}
        maxWidth={maxWidth}
        borderRadius={borderRadius}
        backgroundColor={getColorFromStory(backgroundColor)}
        enableShadow={enableShadow}
        horizontalPadding={getSpacingFromStory(horizontalPadding)}
        horizontalPaddingMobile={getSpacingFromStory(horizontalPaddingMobile)}
        verticalPadding={getSpacingFromStory(verticalPadding)}
        verticalPaddingMobile={getSpacingFromStory(verticalPaddingMobile)}
      >
        {body.map((blokData) => (
          <StoryblokComponent blok={blokData} key={blokData._uid} />
        ))}

        {!!errorMessage && (
          <FormBuilderFormErrorMessage>{errorMessage}</FormBuilderFormErrorMessage>
        )}
        {!!successMessage && (
          <FormBuilderFormSuccessMessage>{successMessage}</FormBuilderFormSuccessMessage>
        )}
      </FormBuilderWrapper>
    </FormBuilderContextProvider>
  );
};
